import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import core, { App, GQL } from '@gin-betting/core';
import { OrderedSet } from 'immutable';

import { State } from 'types';
import { useMemoSelector } from 'hooks';
import { useAsyncModule } from 'async-modules';
import { Ref } from './types';
import {
    bannerMatchIdsSelector,
    favoritesIdsHandler,
    makeIdsSelectorByRoute,
} from './utils';

interface Input {
    route: App.Routes;
}

const {
    entities: {
        actions: { getOddsStatistic },
        selectors: { selectMatches },
    },
} = core;

const selectOutrightIds = createSelector(
    selectMatches,
    (matches): IterableIterator<string> =>
        matches
            .filter(({ type }) => type === GQL.SportEventType.OUTRIGHT)
            .keys()
);

function useMatchListIds(route: App.Routes) {
    const matchModule = useAsyncModule<'match'>('match');

    const selectProps = useMemo(
        () => makeIdsSelectorByRoute(route, matchModule),
        [route, matchModule]
    );

    return useSelector<State, ReturnType<typeof selectProps>>(state =>
        selectProps(state)
    );
}

function useOddsStatisticFetcher(input: Input) {
    const { route } = input;

    const dispatch = useDispatch();
    const cache = useRef<Ref>({
        allMatchIds: OrderedSet<string>(),
    });

    const { allMatchIds: prevAllMatchIds } = cache.current;

    const matchesListIdsByRoute = useMatchListIds(route);

    const outrightIds = useMemoSelector(selectOutrightIds);

    /* TODO filter by path */
    const favoriteMachIds = useMemoSelector(favoritesIdsHandler, input);
    const topMatchesIds = useSelector(bannerMatchIdsSelector);

    const matchIds = matchesListIdsByRoute
        .concat(outrightIds)
        .concat(favoriteMachIds)
        .concat(topMatchesIds)
        .filter(Boolean);

    const nextRequestedIds = matchIds.subtract(prevAllMatchIds);
    const allMatchIds = prevAllMatchIds.concat(matchIds);

    cache.current = {
        allMatchIds,
    };

    useEffect(() => {
        if (nextRequestedIds.size) {
            const matchIdsArr = nextRequestedIds.toSeq().toArray();
            dispatch(getOddsStatistic(matchIdsArr));
        }
    }, [dispatch, nextRequestedIds]);
}

export default useOddsStatisticFetcher;
